import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
	Button,
	Checkbox,
	Input,
	Popover,
	Table,
	Tag,
	Typography,
	Modal,
	Tooltip,
	message,
} from 'antd';
import { Link } from 'react-router-dom';
import NoDataState from '../../../RnRCommonComponent/noDataState/NoDataState.jsx';
import uuid from 'uuid/v4';
import { useSelector } from 'react-redux';
import {
	DownloadOutlined,
	EyeInvisibleOutlined,
	InfoCircleOutlined,
	MailOutlined,
} from '@ant-design/icons';
import { addCommas } from '../../../rewardsAndRecognitionHelper/utils.js';
import { parse } from '../../../_shared/services/utils.js';
import { CoreBadge } from '../../../RnRCommonComponent';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
} from '../../../RnRCommonComponent/CoreBadge/rewardsConstants.js';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const ApprovedContainer = (props) => {
	const {
		tableData,
		isPending = false,
		isApprove,
		isDecline,
		setIsDecline,
		setIsApprove,
		handleApprovalClick,
		handleDeclineClick,
		handleApproveAndDeclined,
		pendingRecordId,
		pendingRecord,
		getJustificationContent,
	} = props;
	const currentUser = useSelector((state) => state.user.currentUser);
	const recognitionSettings = currentUser?.company?.recognitionSettings || {};
	const { requireJustification, baseSnappyBudget = 0 } = recognitionSettings;
	const [showJustificationModal, setShowJustificationModal] = useState(false);
	const [selectedJustification, setSelectedJustification] = useState([]);
	const [showShoutoutModal, setShowShoutoutModal] = useState(false);
	const [selectedShoutout, setSelectedShoutout] = useState(null);
	const [declineReason, setDeclineReason] = useState('');
	const [quietDecline, setQuietDecline] = useState(false);
	const [isProcessingApproval, setIsProcessingApproval] = useState(false);

	useEffect(() => {
		if (!isApprove) {
			setIsProcessingApproval(false);
		}
	}, [isApprove]);

	const statusColorMap = (s, o = 1) =>
		s === 'Approved'
			? `oklch(0.7903 0.136 192.97 / ${o})`
			: s === 'Declined'
				? `oklch(0.58 0.26 295 / ${o})`
				: `oklch(0.6757 0.187 349.98 / ${o})`;
	const getJobRegion = (ac) => {
		const customValues = parse(ac?.customValues);
		return customValues?.JobRegion || null;
	};

	const getRecipientDetails = (record) => {
		if (
			record?.recipients &&
			Array.isArray(record.recipients) &&
			record.recipients.length > 0
		) {
			return record.recipients.map((recipient) => {
				const user = recipient?.user;
				const ac = recipient?.accountClaim;
				const name =
					user?.firstName || user?.lastName
						? `${user.firstName || ''} ${user.lastName || ''}`.trim()
						: ac?.firstName || ac?.lastName
							? `${ac.firstName || ''} ${ac.lastName || ''}`.trim()
							: 'Unknown Recipient';
				const emailAddress = user?.emailAddress || ac?.emailAddress || null;
				const employeeId =
					user?.employeeId || ac?.employeeId || ac?.uniqueEmployeeId || null;
				const branchLocation = ac?.branchLocation || null;
				const jobRegion = getJobRegion(ac);
				const userId = user ? recipient.userId || user.id : null;
				return {
					name,
					emailAddress,
					employeeId,
					branchLocation,
					jobRegion,
					userId,
				};
			});
		}

		if (record?.userId === 'account_claim' && record?.accountClaim) {
			const ac = record.accountClaim;
			return [
				{
					name:
						`${ac.firstName || ''} ${ac.lastName || ''}`.trim() ||
						'Account Claim (No Name)',
					emailAddress: ac.emailAddress || null,
					employeeId: ac.employeeId || ac.uniqueEmployeeId || null,
					branchLocation: ac.branchLocation || null,
					jobRegion: getJobRegion(ac),
					userId: null,
				},
			];
		}

		if (record?.user?.firstName || record?.user?.lastName) {
			return [
				{
					name: `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim(),
					emailAddress: record.user.emailAddress || null,
					employeeId: null,
					branchLocation: null,
					jobRegion: null,
					userId: record.userId || record.user.id,
				},
			];
		}

		return [
			{
				name: 'Unknown Recipient',
				emailAddress: null,
				employeeId: null,
				branchLocation: null,
				jobRegion: null,
				userId: null,
			},
		];
	};

	const renderRecipientPopover = (details) => (
		<div
			style={{
				maxWidth: 300,
				maxHeight: 300,
				overflowY: 'auto',
				marginBottom: 0,
			}}
		>
			<span style={{ fontWeight: 800, color: 'rgba(0, 0, 0, 0.85)' }}>
				Recipient Details:
			</span>
			<ul style={{ marginBottom: 0 }}>
				{details.map((r, i) => (
					<li key={i} style={{ marginBottom: i < details.length - 1 ? 8 : 0 }}>
						<div>
							<strong>{r.name}</strong>
						</div>
						{r.emailAddress && <div>{r.emailAddress}</div>}
						{r.employeeId && <div>Employee ID: {r.employeeId}</div>}
						{r.branchLocation && <div>Branch: {r.branchLocation}</div>}
					</li>
				))}
			</ul>
		</div>
	);

	const columns = [
		{
			title: 'Recipient(s)',
			dataIndex: 'user',
			width: 180,
			key: 'recipient',
			render: (user, record) => {
				const details = getRecipientDetails(record);

				const recipientElements = details.map((r, index) => {
					if (r.userId) {
						return (
							<Link
								key={index}
								to={`/employees/${r.userId}`}
								style={{ color: 'inherit', textDecoration: 'underline' }}
							>
								{r.name}
							</Link>
						);
					}
					return <span key={index}>{r.name}</span>;
				});

				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<div style={{ lineHeight: '1.6' }}>
							{recipientElements.map((element, index) => (
								<React.Fragment key={index}>
									{element}
									{index < recipientElements.length - 1 && ', '}
								</React.Fragment>
							))}
						</div>
						<Popover
							content={renderRecipientPopover(details)}
							getPopupContainer={(triggerNode) => triggerNode.parentNode}
						>
							<span
								style={{
									fontSize: '14px',
									color: 'var(--lochmara)',
									cursor: 'help',
									marginLeft: '8px',
								}}
							>
								<InfoCircleOutlined />
							</span>
						</Popover>
					</div>
				);
			},
		},

		{
			title: 'Points',
			dataIndex: 'badge',
			width: 80,
			key: 'points',
			render: (badge = {}) => (
				<span style={{ color: 'oklch(0.58 0.26 295)' }}>
					{addCommas(badge?.points || 0)}
				</span>
			),
		},

		{
			title: 'Amount',
			dataIndex: 'badge',
			width: 80,
			key: 'costInCurrency',
			render: (badge = {}) => (
				<span
					style={{
						color:
							badge?.snappyGrant?.costInCurrency === 0 ? 'inherit' : 'green',
					}}
				>
					{'$' + (badge?.snappyGrant?.costInCurrency ?? 0)}
				</span>
			),
		},
		{
			title: 'Budget',
			dataIndex: 'user',
			width: 80,
			key: 'remainingBudget',
			render: (user = {}) => {
				const currentBudget = user?.recognitionEvents?.currentBudget;
				const remainingBudget =
					currentBudget !== undefined ? currentBudget : baseSnappyBudget;
				return (
					<span style={{ color: remainingBudget <= 0 ? 'red' : 'green' }}>
						{'$' + addCommas(remainingBudget)}
					</span>
				);
			},
		},
		{
			title: 'Recognition',
			dataIndex: 'badge',
			width: 180,
			key: 'badgeName',
			render: (badge = {}) => badge?.name || '',
		},
		{
			title: 'Reward',
			dataIndex: 'badge',
			width: 140,
			key: 'reward',
			render: (badge = {}, record) => {
				return record?.sentWithoutSnappyGrant
					? ''
					: badge?.snappyGrant?.name || '';
			},
		},
		{
			title: 'Created By',
			dataIndex: 'awardedByUser',
			width: 150,
			key: 'createdBy',
			render: (awardedByUser = {}, record) => {
				const handleCopyEmail = () => {
					if (awardedByUser?.emailAddress) {
						navigator.clipboard.writeText(awardedByUser.emailAddress);
						message.success('Email copied to clipboard');
					}
				};

				let nameElement;
				if (awardedByUser?.firstName || awardedByUser?.lastName) {
					const name =
						`${awardedByUser.firstName || ''} ${awardedByUser.lastName || ''}`.trim();
					const userId = awardedByUser?.id;

					nameElement = userId ? (
						<Link
							to={`/employees/${userId}`}
							style={{ color: 'inherit', textDecoration: 'underline' }}
						>
							{name}
						</Link>
					) : (
						name
					);
				} else {
					nameElement = record.awardedBy || 'Unknown';
				}

				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						{nameElement}
						{awardedByUser?.emailAddress && (
							<Tooltip title={`Copy ${awardedByUser.emailAddress}`}>
								<MailOutlined
									onClick={handleCopyEmail}
									style={{
										marginLeft: '6px',
										fontSize: '14px',
										color: 'var(--lochmara)',
										cursor: 'pointer',
									}}
								/>
							</Tooltip>
						)}
						{awardedByUser?.employeeId && (
							<Popover
								content={
									<div style={{ maxWidth: 300 }}>
										<span
											style={{ fontWeight: 800, color: 'rgba(0, 0, 0, 0.85)' }}
										>
											Created By Details:
										</span>
										<div>
											<strong>
												{`${awardedByUser.firstName || ''} ${awardedByUser.lastName || ''}`.trim()}
											</strong>
										</div>
										<div>Employee ID: {awardedByUser.employeeId}</div>
									</div>
								}
								getPopupContainer={(triggerNode) => triggerNode.parentNode}
							>
								<span
									style={{
										fontSize: '14px',
										color: 'var(--lochmara)',
										cursor: 'help',
										marginLeft: '6px',
									}}
								>
									<InfoCircleOutlined />
								</span>
							</Popover>
						)}
					</div>
				);
			},
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'created',
			width: 120,
			defaultSortOrder: 'descend',
			sorter: (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
			render: (createdAt, record) =>
				createdAt
					? dayjs(createdAt).format('MM/DD/YYYY')
					: record?.updatedAt
						? dayjs(record.updatedAt).format('MM/DD/YYYY')
						: 'Unknown',
		},
		...(!isPending
			? [
					{
						title: 'Approved',
						dataIndex: 'completedAt',
						key: 'completed',
						width: 120,
						render: (_completedAt, record) =>
							record.completedAt
								? dayjs(record.completedAt).format('MM/DD/YYYY')
								: record.updatedAt
									? dayjs(record.updatedAt).format('MM/DD/YYYY')
									: 'Unknown',
					},
				]
			: []),
		{
			title: requireJustification ? 'Answers' : 'Shoutout',
			dataIndex: requireJustification ? 'justificationFormAnswers' : 'content',
			key: requireJustification ? 'justification' : 'shoutout',
			render: (_data, record) => {
				if (!requireJustification) {
					const hasContent =
						record?.content?.message || record?.content?.gifURL;
					return hasContent ? (
						<Button
							size="small"
							type="default"
							className="approve-btn mr-2"
							onClick={() => {
								const recipientName =
									record?.userId === 'account_claim' && record?.accountClaim
										? `${record.accountClaim.firstName} ${record.accountClaim.lastName}`
										: `${record?.user?.firstName || ''} ${record?.user?.lastName || ''}`.trim();

								setSelectedShoutout({
									content: record?.content,
									badgeName: record?.badge?.name,
									recipient: recipientName,
									createdBy: record?.awardedByUser
										? `${record?.awardedByUser?.firstName || ''} ${record?.awardedByUser?.lastName || ''}`.trim()
										: record?.awardedBy || 'Unknown',
									created: record?.createdAt
										? dayjs(record?.createdAt).format('MM/DD/YYYY')
										: record?.updatedAt
											? dayjs(record?.updatedAt).format('MM/DD/YYYY')
											: 'Unknown',
									points: record?.badge?.points ?? 0,
									bonusPoints: record?.badge?.bonusPoints ?? 0,
									badge: record?.badge,
									rawData: record,
								});
								setShowShoutoutModal(true);
							}}
						>
							View
						</Button>
					) : (
						<div
							style={{
								width: '100%',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'rgb(0,0,0,.5)',
								fontWeight: 600,
								cursor: 'default',
							}}
						>
							<Tooltip title="No message included">
								<span>—</span>
							</Tooltip>
						</div>
					);
				} else {
					const justificationContent = getJustificationContent(
						record.justificationFormAnswers
					);
					return justificationContent.length > 0 ? (
						<Button
							size="small"
							type="default"
							className="approve-btn mr-2"
							onClick={() => {
								setSelectedJustification(justificationContent);
								setShowJustificationModal(true);
							}}
						>
							View
						</Button>
					) : (
						<div
							style={{
								width: '100%',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'rgb(0,0,0,.5)',
								fontWeight: 600,
								cursor: 'default',
							}}
						>
							<Tooltip title="No justification provided">
								<span>—</span>
							</Tooltip>
						</div>
					);
				}
			},
		},
		...(!isPending
			? [
					{
						title: 'Status',
						dataIndex: 'approvalStatus',
						key: 'status',
						render: (approvalStatus, record) => (
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<Tag
									style={{
										color: statusColorMap(approvalStatus),
										borderColor: statusColorMap(approvalStatus, 0.4),
										backgroundColor: statusColorMap(approvalStatus, 0.04),
									}}
								>
									{approvalStatus ?? 'Pending'}
								</Tag>
								{approvalStatus === 'Declined' && record?.quietDecline && (
									<Tooltip
										arrowPointAtCenter
										placement="topRight"
										title="Quietly declined — no notification was sent and this will not appear in the sender's activity."
									>
										<EyeInvisibleOutlined
											style={{
												color: 'var(--silver-chalice)',
												fontSize: 18,
												marginRight: 4,
											}}
										/>
									</Tooltip>
								)}
								{record?.declineReason && (
									<Tooltip
										arrowPointAtCenter
										placement="topRight"
										title={record.declineReason}
									>
										<i
											className="icon-message-outline"
											alt=""
											style={{ color: 'var(--silver-chalice)', fontSize: 18 }}
										/>
									</Tooltip>
								)}
								{record?.sentWithoutSnappyGrant && (
									<Tooltip
										arrowPointAtCenter
										placement="topRight"
										title="Recognition Only"
									>
										<i
											className="icon-message-outline"
											alt=""
											style={{ color: 'var(--silver-chalice)', fontSize: 18 }}
										/>
									</Tooltip>
								)}
							</div>
						),
					},
				]
			: []),
		...(isPending
			? [
					{
						title: 'Approval',
						key: 'approval',
						render: (_, record) => (
							<div style={{ display: 'flex', gap: '8px' }}>
								<Button
									size={'small'}
									type={'primary'}
									className="approve-btn"
									onClick={() => {
										handleApprovalClick(record);
									}}
								>
									Approve
								</Button>
								<Button
									size={'small'}
									type={'danger'}
									className="decline-btn"
									onClick={() => {
										handleDeclineClick(record);
									}}
								>
									Decline
								</Button>
							</div>
						),
					},
				]
			: []),
	];

	const exportToCSV = () => {
		try {
			if (!tableData || tableData.length === 0) {
				message.warning('No data to export');
				return;
			}

			// Columns that only appear when at least one row has a value, so
			// companies that don't use a field don't get an empty column.
			const OPTIONAL_HEADERS = [
				'Recipient Branch Location',
				'Recipient Job Region',
				'Created By Branch Location',
				'Created By Job Region',
			];
			const allHeaders = [
				'Recipient',
				'Recipient Employee ID',
				'Recipient Branch Location',
				'Recipient Job Region',
				'Points',
				'Amount',
				'Budget',
				'Recognition',
				'Shoutout Message',
				'Reward',
				'Created By',
				'Created By Email',
				'Created By Employee ID',
				'Created By Branch Location',
				'Created By Job Region',
				'Created',
				...(isPending ? [] : ['Approved', 'Status']),
			];

			const rows = [];

			tableData.forEach((record) => {
				const details = getRecipientDetails(record);
				const points = addCommas(record?.badge?.points || 0);
				const amount = '$' + (record?.badge?.snappyGrant?.costInCurrency ?? 0);

				const currentBudget = record?.user?.recognitionEvents?.currentBudget;
				const budget =
					'$' +
					addCommas(
						currentBudget !== undefined ? currentBudget : baseSnappyBudget
					);

				const recognition = record?.badge?.name || '';
				const shoutoutMessage = record?.content?.message || '';
				const reward = record?.sentWithoutSnappyGrant
					? ''
					: record?.badge?.snappyGrant?.name || '';

				const createdBy =
					record?.awardedByUser?.firstName || record?.awardedByUser?.lastName
						? `${record.awardedByUser.firstName || ''} ${record.awardedByUser.lastName || ''}`.trim()
						: record?.awardedBy || 'Unknown';

				const createdByEmail = record?.awardedByUser?.emailAddress || '';

				const createdByEmployeeId = record?.awardedByUser?.employeeId || '';

				const createdByAccountClaim = record?.awardedByUser?.accountClaim;
				const createdByBranchLocation =
					createdByAccountClaim?.branchLocation || '';
				const createdByJobRegion = getJobRegion(createdByAccountClaim) || '';

				const created = record?.createdAt
					? dayjs(record.createdAt).format('MM/DD/YYYY')
					: record?.updatedAt
						? dayjs(record.updatedAt).format('MM/DD/YYYY')
						: 'Unknown';

				const baseRowData = {
					Points: points,
					Amount: amount,
					Budget: budget,
					Recognition: recognition,
					'Shoutout Message': shoutoutMessage,
					Reward: reward,
					'Created By': createdBy,
					'Created By Email': createdByEmail,
					'Created By Employee ID': createdByEmployeeId,
					'Created By Branch Location': createdByBranchLocation,
					'Created By Job Region': createdByJobRegion,
					Created: created,
					...(isPending
						? {}
						: {
								Approved: record.completedAt
									? dayjs(record.completedAt).format('MM/DD/YYYY')
									: record.updatedAt
										? dayjs(record.updatedAt).format('MM/DD/YYYY')
										: 'Unknown',
								Status: record?.approvalStatus ?? 'Pending',
							}),
				};

				// Create a row for each recipient
				details.forEach((recipient) => {
					rows.push({
						Recipient: recipient.name,
						'Recipient Employee ID': recipient.employeeId || '',
						'Recipient Branch Location': recipient.branchLocation || '',
						'Recipient Job Region': recipient.jobRegion || '',
						...baseRowData,
					});
				});
			});

			const headers = allHeaders.filter(
				(header) =>
					!OPTIONAL_HEADERS.includes(header) || rows.some((row) => row[header])
			);

			const csvContent = [
				headers.join(','),
				...rows.map((row) =>
					headers
						.map((header) => row[header] ?? '')
						.map((cell) => {
							const cellStr = String(cell);
							if (
								cellStr.includes(',') ||
								cellStr.includes('"') ||
								cellStr.includes('\n')
							) {
								return `"${cellStr.replace(/"/g, '""')}"`;
							}
							return cellStr;
						})
						.join(',')
				),
			].join('\n');

			const timestamp = new Date()
				.toISOString()
				.replace(/[:.]/g, '-')
				.slice(0, -5);

			const tableType = isPending ? 'pending-approval' : 'approval-status';

			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const link = document.createElement('a');
			if (link.download !== undefined) {
				const url = URL.createObjectURL(blob);
				link.setAttribute('href', url);
				link.setAttribute(
					'download',
					`recognitions-${tableType}-${timestamp}.csv`
				);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}

			message.success('Data exported successfully!');
		} catch (error) {
			console.error('Error exporting to CSV:', error);
			message.error('Failed to export data');
		}
	};

	return (
		<div className="mt-4">
			<div
				className="page-title"
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<h2 className="page-heading-manage-reward">
					{isPending ? 'Pending Approval' : 'Approval Status'}
				</h2>
				<Button
					type="default"
					icon={<DownloadOutlined />}
					onClick={exportToCSV}
					disabled={!tableData || tableData.length === 0}
				>
					Export to CSV
				</Button>
			</div>
			<div className="nomination-rewards-table table-card">
				<Table
					rowKey={(record) => uuid() + record.id}
					dataSource={tableData}
					columns={columns}
					rowHoverable={false}
					rowSelectedHoverBg={'white'}
					pagination={{ pageSize: 25, showSizeChanger: false }}
					locale={{
						emptyText: (
							<NoDataState
								title={
									isPending
										? 'There are no currently pending recognitions.'
										: 'You have not approved any recognitions yet.'
								}
							/>
						),
					}}
				/>
			</div>
			<Modal
				destroyOnClose
				width={420}
				open={isApprove}
				title="Confirm Approval"
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => {
					setIsApprove(false);
				}}
			>
				<>
					<p className="text-center modal-detail">
						Are you sure you want to approve?
					</p>
					<div className="text-center">
						<Button
							className="approve-btn"
							size={'large'}
							type="primary"
							loading={isProcessingApproval}
							disabled={isProcessingApproval}
							onClick={async () => {
								setIsProcessingApproval(true);
								await handleApproveAndDeclined(
									pendingRecordId,
									'Approved',
									pendingRecord
								);
							}}
						>
							{isProcessingApproval ? 'Processing...' : 'Approve'}
						</Button>
					</div>
				</>
			</Modal>
			<Modal
				destroyOnClose
				width={420}
				title="Decline Approval"
				open={isDecline}
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => {
					setIsDecline(false);
					setQuietDecline(false);
				}}
			>
				<>
					<p className="text-center modal-detail">
						Are you sure you want to decline?
					</p>
					<div className="text-center modal-detail">
						<TextArea
							placeholder="Please Provide a Reason"
							value={declineReason}
							onChange={(e) => {
								setDeclineReason(e.target.value);
							}}
							className="custom-input"
						/>
					</div>
					<div className="modal-detail" style={{ marginBottom: 16 }}>
						<Checkbox
							checked={quietDecline}
							onChange={(e) => {
								setQuietDecline(e.target.checked);
							}}
						>
							Do not send decline notification and do not show in sender's
							activity
						</Checkbox>
					</div>
					<div className="text-center">
						<Button
							className="decline-btn"
							disabled={!declineReason}
							type="primary"
							danger={true}
							onClick={() => {
								handleApproveAndDeclined(
									pendingRecordId,
									'Declined',
									null,
									declineReason,
									quietDecline
								);
								setQuietDecline(false);
							}}
						>
							Decline
						</Button>
					</div>
				</>
			</Modal>
			<Modal
				destroyOnClose
				width={420}
				open={showJustificationModal}
				title="Justification Answers"
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => setShowJustificationModal(false)}
			>
				<div style={{ padding: '0 12px', fontSize: '16px' }}>
					{selectedJustification && Array.isArray(selectedJustification) ? (
						selectedJustification.map((j, i) => (
							<div key={i}>
								<span>
									<strong>
										{j?.question ? j.question + ':' : 'Question:'}
									</strong>
								</span>
								<p>{j?.answer || 'No answer provided'}</p>
							</div>
						))
					) : (
						<p>No justification data available</p>
					)}
				</div>
			</Modal>
			<Modal
				destroyOnClose
				width={600}
				open={showShoutoutModal}
				title="Recognition Shoutout"
				footer={false}
				className="confirmation-modal"
				onOk={false}
				onCancel={() => setShowShoutoutModal(false)}
			>
				<div style={{ padding: '20px 0' }}>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							padding: '16px',
							backgroundColor: '#f8f9fa',
							borderRadius: '8px',
							marginBottom: '20px',
						}}
					>
						{selectedShoutout?.badge?.id && (
							<div className="feed-reward-logo" style={{ marginRight: '16px' }}>
								<CoreBadge
									useTilt={false}
									badgeType={
										REVERSE_MAPING_BADGE_TYPE[
											selectedShoutout.badge.badgeTypeId
										]
									}
									label={
										selectedShoutout.badge.label ||
										selectedShoutout.badge.name ||
										'Badge'
									}
									iconCode={
										ICON_COMPLETE_MAP.find(
											(icon) => icon.id === selectedShoutout.badge.iconId
										)?.code
									}
									color={selectedShoutout.badge.color}
									iconColor={selectedShoutout.badge.iconColor}
									gradientColor={selectedShoutout.badge.gradientColor}
									shape={selectedShoutout.badge.shape}
									badgeRotation={selectedShoutout.badge.badgeRotation}
									customImageSignedUrl={
										selectedShoutout.badge.customImageSignedUrl
									}
									labelColor={selectedShoutout.badge.labelColor}
									customIconSignedUrl={
										selectedShoutout.badge.customIconSignedUrl
									}
									hideSnappyIcon={true}
								/>
							</div>
						)}
						<div style={{ flex: 1 }}>
							<div
								style={{
									fontSize: '18px',
									fontWeight: 'bold',
									marginBottom: '4px',
								}}
							>
								{selectedShoutout?.createdBy} recognized{' '}
								{selectedShoutout?.recipient}
							</div>
							<div
								style={{ fontSize: '16px', color: '#666', marginBottom: '4px' }}
							>
								{selectedShoutout?.badgeName}
							</div>
							<div
								style={{
									fontSize: '14px',
									color: '#595959',
									display: 'flex',
									alignItems: 'center',
									gap: '12px',
								}}
							>
								<span>
									{dayjs(selectedShoutout?.created).format('MMM DD, YYYY')}
								</span>
								{selectedShoutout?.badge?.id && selectedShoutout?.points && (
									<span>• {addCommas(selectedShoutout?.points)} points</span>
								)}
							</div>
						</div>
					</div>
					{selectedShoutout?.content?.message ? (
						<div
							style={{
								marginBottom: '20px',
								padding: '16px',
								backgroundColor: 'white',
								border: '1px solid #e0e0e0',
								borderRadius: '8px',
							}}
						>
							<Text
								style={{
									fontSize: '16px',
									lineHeight: '1.6',
									fontStyle: 'italic',
								}}
							>
								"{selectedShoutout.content.message}"
							</Text>
						</div>
					) : (
						<div
							style={{
								marginBottom: '20px',
								padding: '16px',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								textAlign: 'center',
							}}
						>
							<Text
								style={{ fontSize: '16px', color: '#666', fontStyle: 'italic' }}
							>
								No message included
							</Text>
						</div>
					)}
					{selectedShoutout?.content?.gifURL && (
						<div style={{ textAlign: 'center' }}>
							<img
								src={selectedShoutout.content.gifURL}
								alt="Shoutout GIF"
								style={{
									maxWidth: '100%',
									maxHeight: '300px',
									borderRadius: '8px',
									boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
								}}
							/>
						</div>
					)}
					{isPending && (
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '8px',
								marginTop: '20px',
							}}
						>
							<Button
								type="primary"
								className="approve-btn"
								onClick={() => {
									setShowShoutoutModal(false);
									handleApprovalClick(selectedShoutout?.rawData);
								}}
							>
								Approve
							</Button>
							<Button
								type="danger"
								className="decline-btn"
								onClick={() => {
									setShowShoutoutModal(false);
									handleDeclineClick(selectedShoutout?.rawData);
								}}
							>
								Decline
							</Button>
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
};

export default ApprovedContainer;
